import axios from 'axios';

// SWA-311 · único punto de lectura de REACT_APP_BACKEND_URL. "" = mismo dominio.
export const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

// S2/S2.2 · helper que añade idempotency_key fresco (UUIDv4) a cada submit.
// El cliente lo genera POR submit (no derivado de contenido) para bloquear doble-click
// pero respetar la intención de crear "el mismo recurso" cuando el user re-abre el form.
function freshKey() {
  if (typeof window !== 'undefined' && window.crypto && window.crypto.randomUUID) {
    return window.crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2, 14)}`;
}
export function withIdempotencyKey(payload) {
  return { ...(payload || {}), idempotency_key: freshKey() };
}

/**
 * S3 cola de runs · reintento con backoff limitado contra 409 RUN_IN_PROGRESS.
 *
 * Regla:
 *  - Si recibimos 409 con detail.code === 'RUN_IN_PROGRESS', leemos `active_run.expires_at`
 *    y esperamos un backoff exponencial acotado por ese horizonte (NO esperamos pasado).
 *  - Máximo `MAX_ATTEMPTS = 3` intentos en TOTAL (1 inicial + 2 reintentos).
 *  - Si tras los 3 sigue 409, propagamos el error con un mensaje claro para que la UI lo
 *    muestre tal cual ("otro usuario está corriendo este escenario, intenta en un momento").
 *  - Cualquier otro status code (500, 400, 404…) NO se reintenta — propaga inmediato.
 *
 * No reintenta infinito (un lock genuinamente atorado debe hacerse visible al usuario).
 */
export const MAX_RUN_ATTEMPTS = 3;
const MIN_BACKOFF_MS = 800;
const MAX_BACKOFF_MS = 8_000;

/**
 * Extrae un mensaje legible para `toast.error()` desde un error axios.
 * Maneja strings (`detail: "..."`) y objetos enriquecidos (`detail: {code, message, friendly_message}`).
 */
export function extractErrorMessage(err, fallback = 'Error inesperado') {
  const detail = err?.response?.data?.detail;
  if (!detail) return err?.message || fallback;
  if (typeof detail === 'string') return detail;
  if (typeof detail === 'object') {
    return detail.friendly_message || detail.message || detail.code || fallback;
  }
  return String(detail);
}

async function runWithRetry(payload) {
  let lastErr = null;
  for (let attempt = 1; attempt <= MAX_RUN_ATTEMPTS; attempt++) {
    try {
      return await apiClient.post('/analyzer/run', payload);
    } catch (err) {
      lastErr = err;
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      const code = (typeof detail === 'object' && detail !== null) ? detail.code : null;
      if (status !== 409 || code !== 'RUN_IN_PROGRESS') {
        throw err;  // no es race condition → propagar inmediato
      }
      if (attempt === MAX_RUN_ATTEMPTS) break;  // 3er intento ya falló, no esperar
      // Backoff: exponencial pero acotado por (expires_at - now)
      const exp = Math.min(MAX_BACKOFF_MS, MIN_BACKOFF_MS * Math.pow(2, attempt - 1));
      let wait = exp;
      const expiresAt = detail?.active_run?.expires_at;
      if (expiresAt) {
        const msUntilExpiry = new Date(expiresAt).getTime() - Date.now();
        if (msUntilExpiry > 0) wait = Math.min(wait, msUntilExpiry + 100);
        else wait = 100;  // el lock ya expiró → reintenta casi inmediato
      }
      await new Promise(res => setTimeout(res, Math.max(100, wait)));
    }
  }
  // Tras MAX_ATTEMPTS · enriquece el error con mensaje claro para la UI
  const enrichedDetail = (lastErr?.response?.data?.detail) || {};
  const friendly = (typeof enrichedDetail === 'object' && enrichedDetail.code === 'RUN_IN_PROGRESS')
    ? 'Otro usuario está corriendo este escenario. Espera un momento y vuelve a intentarlo.'
    : (typeof enrichedDetail === 'string' ? enrichedDetail : 'Análisis bloqueado tras varios reintentos.');
  if (lastErr?.response?.data) {
    lastErr.response.data.detail = {
      ...(typeof enrichedDetail === 'object' ? enrichedDetail : {}),
      friendly_message: friendly,
      attempts: MAX_RUN_ATTEMPTS,
    };
  }
  throw lastErr;
}

const apiClient = axios.create({
  baseURL: API,
  headers: { 'Content-Type': 'application/json' }
});

// Add auth token to requests
apiClient.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle 401 responses
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

export const authAPI = {
  register: (data) => apiClient.post('/auth/register', data),
  login: (data) => apiClient.post('/auth/login', data),
  profile: () => apiClient.get('/auth/profile'),
};

export const projectsAPI = {
  list: () => apiClient.get('/projects'),
  create: (data) => apiClient.post('/projects', data),
  get: (id) => apiClient.get(`/projects/${id}`),
  update: (id, data) => apiClient.put(`/projects/${id}`, data),
  delete: (id) => apiClient.delete(`/projects/${id}`),
  archive: (id) => apiClient.post(`/projects/${id}/archive`),
  restore: (id) => apiClient.post(`/projects/${id}/restore`),
  duplicate: (id) => apiClient.post(`/projects/${id}/duplicate`),
  activity: (id) => apiClient.get(`/projects/${id}/activity`),
};

export const scenariosAPI = {
  list: (projectId) => apiClient.get(`/projects/${projectId}/scenarios`),
  create: (projectId, data) => apiClient.post(`/projects/${projectId}/scenarios`, withIdempotencyKey(data)),
  get: (projectId, scenarioId) => apiClient.get(`/projects/${projectId}/scenarios/${scenarioId}`),
  update: (projectId, scenarioId, data) => apiClient.put(`/projects/${projectId}/scenarios/${scenarioId}`, data),
  delete: (projectId, scenarioId) => apiClient.delete(`/projects/${projectId}/scenarios/${scenarioId}`),
  lock: (projectId, scenarioId) => apiClient.post(`/projects/${projectId}/scenarios/${scenarioId}/lock`),
  unlock: (projectId, scenarioId) => apiClient.post(`/projects/${projectId}/scenarios/${scenarioId}/unlock`),
  duplicate: (projectId, scenarioId) => apiClient.post(`/projects/${projectId}/scenarios/${scenarioId}/duplicate`),
  updateAssumptions: (projectId, scenarioId, updates) => apiClient.put(`/projects/${projectId}/scenarios/${scenarioId}/assumptions`, { updates }),
};

// ── Sprint 6 · Semana 3.2 — Scenario Templates ──
export const templatesAPI = {
  create: (data) => apiClient.post('/scenario-templates', data),
  list: () => apiClient.get('/scenario-templates'),
  get: (id) => apiClient.get(`/scenario-templates/${id}`),
  update: (id, data) => apiClient.patch(`/scenario-templates/${id}`, data),
  delete: (id) => apiClient.delete(`/scenario-templates/${id}`),
  apply: (id, data) => apiClient.post(`/scenario-templates/${id}/apply`, data),
};

export const analyzerAPI = {
  run: (data) => runWithRetry(data),
  // SWA-240 · calcula y devuelve, sin registrar corrida. Para vistas que se
  // montan solas (Analyzer al abrirse, AforoCard): el PDF ejecutivo lee la
  // corrida más reciente y sólo `run` debe alimentarla.
  preview: (data) => apiClient.post('/analyzer/preview', data),
  stressRun: (data) => apiClient.post('/analyzer/stress-run', data),
  compare: (data) => apiClient.post('/analyzer/compare', data),
  sensitivity: (data) => apiClient.post('/analyzer/sensitivity', data),
  tornado: (data) => apiClient.post('/analyzer/tornado', data),
  getRuns: (projectId) => apiClient.get(`/analyzer/runs/${projectId}`),
  recalculateAll: (data) => apiClient.post('/analyzer/recalculate-all', data),
  getEngineVersion: () => apiClient.get('/analyzer/engine-version'),
  curvePreview: (curve, months) => apiClient.get(`/analyzer/curve-preview?curve=${curve}&months=${months}`),
  // ── Solver / Apalancamiento ──
  maxLand: (data) => apiClient.post('/analyzer/max-land', data),
  maxRate: (data) => apiClient.post('/analyzer/max-rate', data),
  fundingGap: (data) => apiClient.post('/analyzer/funding-gap', data),
  // Sprint 3 · v2.8.4 — Estado de caja consolidado por acreedor
  creditorStatement: (scenarioId) => apiClient.get(`/scenarios/${scenarioId}/creditor-statement`),
};

// ── Sprint 4 · v2.9.0 · Waterfall multi-tranche (LP1/LP2/N + Tier 2 promote) ──
export const waterfallAPI = {
  get: (scenarioId) => apiClient.get(`/scenarios/${scenarioId}/capital-structure`),
  save: (scenarioId, data) => apiClient.put(`/scenarios/${scenarioId}/capital-structure`, data),
  simulateV2: (data) => apiClient.post('/scenarios/waterfall/simulate-v2', data),
};

// ── Structured Inputs (V2 data model) ──
export const structuredInputsAPI = {
  // Aggregate: get all structured data for a scenario
  getAll: (scenarioId) => apiClient.get(`/scenarios/${scenarioId}/structured-inputs`),

  // Typologies
  typologies: {
    list: (scenarioId) => apiClient.get(`/scenarios/${scenarioId}/typologies`),
    create: (scenarioId, data) => apiClient.post(`/scenarios/${scenarioId}/typologies`, withIdempotencyKey(data)),
    bulkCreate: (scenarioId, items) => apiClient.post(`/scenarios/${scenarioId}/typologies/bulk`, items),
    update: (scenarioId, itemId, data) => apiClient.put(`/scenarios/${scenarioId}/typologies/${itemId}`, data),
    delete: (scenarioId, itemId) => apiClient.delete(`/scenarios/${scenarioId}/typologies/${itemId}`),
    deleteAll: (scenarioId) => apiClient.delete(`/scenarios/${scenarioId}/typologies`),
  },

  // Land Schedule
  landSchedule: {
    list: (scenarioId) => apiClient.get(`/scenarios/${scenarioId}/land-schedule`),
    create: (scenarioId, data) => apiClient.post(`/scenarios/${scenarioId}/land-schedule`, data),
    bulkCreate: (scenarioId, items) => apiClient.post(`/scenarios/${scenarioId}/land-schedule/bulk`, items),
    update: (scenarioId, itemId, data) => apiClient.put(`/scenarios/${scenarioId}/land-schedule/${itemId}`, data),
    delete: (scenarioId, itemId) => apiClient.delete(`/scenarios/${scenarioId}/land-schedule/${itemId}`),
    deleteAll: (scenarioId) => apiClient.delete(`/scenarios/${scenarioId}/land-schedule`),
    loadTemplate: (scenarioId) => apiClient.post(`/scenarios/${scenarioId}/land-schedule/load-template`),
  },

  // Budget Items
  budgetItems: {
    list: (scenarioId) => apiClient.get(`/scenarios/${scenarioId}/budget-items`),
    create: (scenarioId, data) => apiClient.post(`/scenarios/${scenarioId}/budget-items`, data),
    bulkCreate: (scenarioId, items) => apiClient.post(`/scenarios/${scenarioId}/budget-items/bulk`, items),
    update: (scenarioId, itemId, data) => apiClient.put(`/scenarios/${scenarioId}/budget-items/${itemId}`, data),
    delete: (scenarioId, itemId) => apiClient.delete(`/scenarios/${scenarioId}/budget-items/${itemId}`),
    deleteAll: (scenarioId) => apiClient.delete(`/scenarios/${scenarioId}/budget-items`),
  },

  // Milestones
  milestones: {
    list: (scenarioId) => apiClient.get(`/scenarios/${scenarioId}/milestones`),
    create: (scenarioId, data) => apiClient.post(`/scenarios/${scenarioId}/milestones`, data),
    bulkCreate: (scenarioId, items) => apiClient.post(`/scenarios/${scenarioId}/milestones/bulk`, items),
    update: (scenarioId, itemId, data) => apiClient.put(`/scenarios/${scenarioId}/milestones/${itemId}`, data),
    delete: (scenarioId, itemId) => apiClient.delete(`/scenarios/${scenarioId}/milestones/${itemId}`),
    deleteAll: (scenarioId) => apiClient.delete(`/scenarios/${scenarioId}/milestones`),
  },

  // Indirect Costs
  indirectCosts: {
    list: (scenarioId) => apiClient.get(`/scenarios/${scenarioId}/indirect-costs`),
    create: (scenarioId, data) => apiClient.post(`/scenarios/${scenarioId}/indirect-costs`, data),
    bulkCreate: (scenarioId, items) => apiClient.post(`/scenarios/${scenarioId}/indirect-costs/bulk`, items),
    update: (scenarioId, itemId, data) => apiClient.put(`/scenarios/${scenarioId}/indirect-costs/${itemId}`, data),
    delete: (scenarioId, itemId) => apiClient.delete(`/scenarios/${scenarioId}/indirect-costs/${itemId}`),
    deleteAll: (scenarioId) => apiClient.delete(`/scenarios/${scenarioId}/indirect-costs`),
  },

  // Collection Structure
  collectionStructure: {
    list: (scenarioId) => apiClient.get(`/scenarios/${scenarioId}/collection-structure`),
    create: (scenarioId, data) => apiClient.post(`/scenarios/${scenarioId}/collection-structure`, data),
    update: (scenarioId, itemId, data) => apiClient.put(`/scenarios/${scenarioId}/collection-structure/${itemId}`, data),
    delete: (scenarioId, itemId) => apiClient.delete(`/scenarios/${scenarioId}/collection-structure/${itemId}`),
    deleteAll: (scenarioId) => apiClient.delete(`/scenarios/${scenarioId}/collection-structure`),
  },

  // Capital Structure & Waterfall
  capitalStructure: {
    get: (scenarioId) => apiClient.get(`/scenarios/${scenarioId}/capital-structure`),
    save: (scenarioId, data) => apiClient.put(`/scenarios/${scenarioId}/capital-structure`, data),
    delete: (scenarioId) => apiClient.delete(`/scenarios/${scenarioId}/capital-structure`),
    simulate: (scenarioId, projectId, cs) => apiClient.post('/scenarios/waterfall/simulate', { scenario_id: scenarioId, project_id: projectId, capital_structure: cs || undefined }),
  },
};

export const companyAPI = {
  getInfo: () => apiClient.get('/company/info'),
  listUsers: () => apiClient.get('/company/users'),
  inviteUser: (data) => apiClient.post('/company/users', data),
  updateUser: (userId, data) => apiClient.put(`/company/users/${userId}`, data),
  deleteUser: (userId) => apiClient.delete(`/company/users/${userId}`),
  transferOwnership: (data) => apiClient.post('/company/transfer-ownership', data),
  getAuditLogs: (params) => apiClient.get('/company/audit-logs', { params }),
  updateModule: (module, active) => apiClient.put('/company/modules', { module, active }),
  uploadLogo: (formData) => apiClient.post('/company/logo', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  deleteLogo: () => apiClient.delete('/company/logo'),
  getEntitlements: () => apiClient.get('/company/entitlements'),
};

export const settingsAPI = {
  get: () => apiClient.get('/settings'),
  updateCompany: (data) => apiClient.put('/settings/company', data),
  updateFinancial: (data) => apiClient.put('/settings/financial', data),
  updateReports: (data) => apiClient.put('/settings/reports', data),
  changePassword: (data) => apiClient.post('/settings/change-password', data),
  updatePreferences: (data) => apiClient.put('/settings/preferences', data),
};

export const phasesAPI = {
  list: (projectId) => apiClient.get(`/projects/${projectId}/phases`),
  create: (projectId, data) => apiClient.post(`/projects/${projectId}/phases`, data),
  update: (projectId, phaseId, data) => apiClient.put(`/projects/${projectId}/phases/${phaseId}`, data),
  delete: (projectId, phaseId) => apiClient.delete(`/projects/${projectId}/phases/${phaseId}`),
};

export const unitsAPI = {
  list: (projectId) => apiClient.get(`/projects/${projectId}/units`),
  create: (projectId, data) => apiClient.post(`/projects/${projectId}/units`, data),
  update: (projectId, unitId, data) => apiClient.put(`/projects/${projectId}/units/${unitId}`, data),
  delete: (projectId, unitId) => apiClient.delete(`/projects/${projectId}/units/${unitId}`),
};

export const dashboardAPI = {
  getStats: () => apiClient.get('/dashboard/stats'),
  getProjectScore: (projectId, scenarioId) => apiClient.get(`/dashboard/project-score/${projectId}${scenarioId ? `?scenario_id=${scenarioId}` : ''}`),
  getDecisions: (projectId, scenarioId) => apiClient.get(`/dashboard/decisions/${projectId}${scenarioId ? `?scenario_id=${scenarioId}` : ''}`),
};

export const reportsAPI = {
  types: () => apiClient.get('/reports/types'),
  generate: (data) => apiClient.post('/reports/generate', data),
  list: (projectId) => apiClient.get(`/reports/list${projectId ? `?project_id=${projectId}` : ''}`),
  get: (reportId) => apiClient.get(`/reports/${reportId}`),
  delete: (reportId) => apiClient.delete(`/reports/${reportId}`),
  regenerate: (reportId) => apiClient.post(`/reports/${reportId}/regenerate`),
  excelUrl: (reportId) => `${apiClient.defaults.baseURL}/reports/${reportId}/excel`,
};

export const marketAPI = {
  listComparables: (projectId) => apiClient.get(`/market/comparables/${projectId}`),
  createComparable: (data) => apiClient.post('/market/comparables', data),
  updateComparable: (id, data) => apiClient.put(`/market/comparables/${id}`, data),
  deleteComparable: (id) => apiClient.delete(`/market/comparables/${id}`),
  getSnapshot: (projectId, scenarioId) => apiClient.get(`/market/snapshot/${projectId}${scenarioId ? `?scenario_id=${scenarioId}` : ''}`),
  getAnalysis: (projectId, scenarioId) => apiClient.get(`/market/analysis/${projectId}?scenario_id=${scenarioId}`),
  getSuggestions: (projectId, scenarioId) => apiClient.get(`/market/suggestions/${projectId}${scenarioId ? `?scenario_id=${scenarioId}` : ''}`),
  simulate: (data) => apiClient.post('/market/simulate', data),
  extractFromText: (data) => apiClient.post('/market/extract', data),
  extractFromUrl: (data) => apiClient.post('/market/extract-url', data),
  getLocationData: (projectId) => apiClient.get(`/market/location-data/${projectId}`),
  getDerived: (projectId, scenarioId) => apiClient.get(`/market/derived/${projectId}${scenarioId ? `?scenario_id=${scenarioId}` : ''}`),
  geocodeProject: (projectId) => apiClient.post(`/market/geocode/${projectId}`),
  getRadiusAnalysis: (projectId) => apiClient.get(`/market/radius-analysis/${projectId}`),
  getCrossValidation: (projectId, scenarioId) => apiClient.get(`/market/cross-validation/${projectId}${scenarioId ? `?scenario_id=${scenarioId}` : ''}`),
  generateAIInsights: (projectId, scenarioId) => apiClient.post(`/market/ai-insights/${projectId}${scenarioId ? `?scenario_id=${scenarioId}` : ''}`),
};

export const institutionAPI = {
  get: () => apiClient.get('/institution'),
  dashboard: () => apiClient.get('/institution/dashboard'),
  listCompanies: () => apiClient.get('/institution/companies'),
  invite: (data) => apiClient.post('/institution/invite', data),
  listInvites: () => apiClient.get('/institution/invites'),
  cancelInvite: (id) => apiClient.delete(`/institution/invites/${id}`),
  join: (code) => apiClient.post(`/institution/join?code=${code}`),
};

export const adminAPI = {
  metrics: () => apiClient.get('/admin/metrics'),
  listCompanies: () => apiClient.get('/admin/companies'),
  updateCompany: (companyId, data) => apiClient.put(`/admin/companies/${companyId}`, data),
  deleteCompany: (companyId) => apiClient.delete(`/admin/companies/${companyId}`),
  updatePlan: (companyId, plan) => apiClient.put(`/admin/companies/${companyId}/plan`, { plan }),
  updateModules: (companyId, modules) => apiClient.put(`/admin/companies/${companyId}/modules`, { modules }),
  makeInstitution: (companyId) => apiClient.post(`/admin/companies/${companyId}/make-institution`),
  removeInstitution: (instId) => apiClient.delete(`/admin/institutions/${instId}`),
  listUsers: () => apiClient.get('/admin/users'),
  purgeQaData: (dryRun = true) => apiClient.post('/admin/purge-qa-data', null, { params: { dry_run: dryRun } }),
  resetCompanyMetrics: (companyId) => apiClient.post(`/admin/companies/${companyId}/reset-metrics`),
  restoreCompanyMetrics: (companyId) => apiClient.post(`/admin/companies/${companyId}/restore-metrics`),
  // Cross-tenant user management (super_admin)
  listCompanyUsers: (companyId) => apiClient.get(`/admin/companies/${companyId}/users`),
  createCompanyUser: (companyId, data) => apiClient.post(`/admin/companies/${companyId}/users`, data),
  changeUserRole: (userId, role) => apiClient.put(`/admin/users/${userId}/role`, { role }),
  moveUser: (userId, targetCompanyId) => apiClient.put(`/admin/users/${userId}/move`, { target_company_id: targetCompanyId }),
  deleteUser: (userId) => apiClient.delete(`/admin/users/${userId}`),
  resetUserPassword: (userId, newPassword) => apiClient.post(`/admin/users/${userId}/reset-password`, { new_password: newPassword }),
  impersonate: (userId) => apiClient.post(`/admin/impersonate/${userId}`),
  createTenant: (data) => apiClient.post('/admin/tenants', data),
};

export default apiClient;
